import { useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import {
  PencilSolidV6,
  PlusSolidV6,
  SpinnerSolidV6,
  CircleInfoSolidV6,
  XmarkSolidV6,
} from "@shared/v2/Icomoon";
import AvatarGroup from "@shared/v2/AvatarGroup/AvatarGroup";
import Avatar from "@shared/v2/Avatar";
import Button from "@shared/v2/Button";
import IconButton from "@shared/v2/IconButton";
import Pagination from "@shared/v2/Pagination";
import Dropdown from "@shared/v2/Dropdown";
import Tooltip from "@shared/v2/Tooltip";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import { LeadAssignInfo, CallRecordingInfo } from "./shared";
import TrackingNumberModal from "./TrackingNumberModal";
import { Section } from "../../../Reports/components/BusinessDashboard";
import Error from "../../../Reports/components/DashboardShared/Error";
import SettingsHeader from "./SettingsHeader";
import PlanInfo from "./PlanInfo";
import { useCurrentUser, useFeatureFlags } from "../../../reducers/layoutReducer/selectors";
import useActiveProducts from "../../../Products/hooks/useActiveProducts";
import { PhoneSystemProductIds, ProductFamilyIds } from "../../../Products/constants";
import PurchaseBarContainer from "../../../Products/PurchaseBarContainer";
import Typography from "@shared/v2/Typography";
import useProducts from "../../../Products/hooks/useProducts";
import Step1 from "../../../Products/Step1";
import Backdrop from "../../../Products/Backdrop";
import PhoneSystemChangeModal from "./PhoneSystemChangeModal/PhoneSystemChangeModal";
import { Transition } from "react-transition-group";
import Modal from "@shared/v2/Modal";

const TrackingNumbers = ({
  accountOwnerId,
  agentsWithRoutingNumbers,
  loadingSettings,
  sources,
  trackingNumbers,
}) => {
  const currentUser = useCurrentUser();
  const nodeRef = useRef < HTMLDivElement > null;
  const [showChangePlanBar, setShowChangePlanBar] = useState(false);
  const [newProduct, setNewProduct] = useState(null);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [numberToEdit, setNumberToEdit] = useState(null);
  const [error, setError] = useState("");
  const [numbersToDisplay, setNumbersToDisplay] = useState([]);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const products = useProducts(ProductFamilyIds.PhoneSystem);
  const { actions, activeProducts } = useActiveProducts(ProductFamilyIds.PhoneSystem);
  const accountProduct = activeProducts?.[0];
  const maxNumbers = accountProduct?.product.details.trackingNumberMax || 0;

  const handleChangeSubscription = async (accountProductId, newProductId) => {
    try {
      await caseTransformingAxios.post("/products/change_subscription", {
        accountProductId,
        newProductId,
      });
    } catch (requestError) {
      throw new Error(requestError?.response?.data?.error || "Failed to change subscription");
    }

    // Refresh the page after successful subscription change
    refreshPage();
  };

  const addTrackingNumber = () => {
    setShowTrackingModal(true);
  };

  const editTrackingNumber = (trackingNumber) => {
    let cleanedUsers = [];
    if (trackingNumber?.users && trackingNumber?.users?.length > 0) {
      cleanedUsers = trackingNumber.users.filter((user) => !user.isFallbackUser && !user.isInboundSmsUser);
    }
    setNumberToEdit({ ...trackingNumber, users: cleanedUsers });
    setShowTrackingModal(true);
  };

  const closeModal = () => {
    setShowTrackingModal(false);
    setNumberToEdit(null);
  };

  const refreshPage = (view = "logs") => {
    const url = new URL(window.location.href);
    url.searchParams.set("view", view);
    window.location.href = url.toString();
  };

  useEffect(() => {
    setNumbersToDisplay(trackingNumbers.slice(offset, offset + pageSize));
  }, [trackingNumbers, offset, pageSize]);

  return (
    <>
      <div className="tw-p-[16px] tw-flex-1 tw-flex tw-flex-col tw-gap-[8px]">
        <SettingsHeader />

        <PlanInfo
          loadingSettings={loadingSettings}
          accountProduct={accountProduct}
          trackingNumbers={trackingNumbers}
          cancelProduct={actions.cancelProduct}
          onChangePlan={() => setShowChangePlanBar(true)}
        />

        <Section className="tw-flex tw-flex-col tw-gap-[32px]">
          <div className="tw-flex tw-justify-between tw-items-center tw-flex-wrap tw-gap-[16px]">
            <div className="tw-flex tw-flex-col tw-gap-[8px]">
              <h2 className="tw-m-0 tw-text-18d tw-font-bold">Tracking Numbers</h2>
              <p className="tw-m-0">
                {currentUser.account.isAdmin ? (
                  <>
                    If you have a number you’d like to port to use as a tracking number, please contact
                    support.
                  </>
                ) : (
                  <>
                    You are currently assigned to{" "}
                    <b>
                      {
                        trackingNumbers.filter((tn) => tn.users.some((user) => user.id === currentUser.id))
                          .length
                      }{" "}
                      tracking numbers.
                    </b>
                  </>
                )}
              </p>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-[8px] tw-items-center">
              {currentUser.account.isAdmin && (
                <>
                  <Button
                    disabled={
                      loadingSettings ||
                      (!(currentUser.account.isPlace || currentUser.account.isLever) &&
                        accountProduct?.product.slug !== PhoneSystemProductIds.Enterprise &&
                        trackingNumbers.length >= maxNumbers)
                    }
                    size="medium"
                    onClick={addTrackingNumber}
                  >
                    <div className="tw-flex tw-justify-center tw-items-center tw-gap-6px">
                      <PlusSolidV6 />
                      Add Tracking Number
                    </div>
                  </Button>
                  <span className="tw-text-12d">
                    {trackingNumbers.length}{" "}
                    {currentUser.account.isPlace || currentUser.account.isLever ? "" : `/ ${maxNumbers} `}
                    Tracking Numbers Used
                  </span>
                </>
              )}
            </div>
          </div>
          <Error message={error} show={Boolean(error)} onClose={() => setError("")} />
          {loadingSettings ? (
            <div className="tw-my-[88px] tw-flex tw-items-center tw-justify-center tw-h-full">
              <SpinnerSolidV6 className="tw-h-[32px] tw-w-[32px] tw-animate-spin" />
            </div>
          ) : (
            <>
              {trackingNumbers.length > 0 ? (
                <>
                  <div className="tw-mb-16px">
                    <TrackingNumbersTable
                      trackingNumbers={numbersToDisplay}
                      sources={sources}
                      onClickEdit={editTrackingNumber}
                    />
                  </div>
                  {trackingNumbers.length > 10 && (
                    <PageBar
                      totalCount={trackingNumbers.length}
                      currentOffset={offset}
                      pageSize={pageSize}
                      onChangePage={setOffset}
                      onChangePageSize={setPageSize}
                    />
                  )}
                </>
              ) : (
                <div className="tw-flex tw-flex-col tw-mt-[66px] tw-mb-[50px] tw-items-center tw-justify-center tw-gap-[18px]">
                  <span className="tw-text-18px tw-whitespace-nowrap">
                    Looks like you have no tracking numbers active.
                  </span>
                  <span className="tw-max-w-[750px] tw-text-center">
                    Make it easier to connect with your leads, gain insight into your marketing campaigns, and
                    understand your ROI like never before with Tracking Numbers.
                  </span>
                </div>
              )}
            </>
          )}

          {showTrackingModal && (
            <TrackingNumberModal
              isNewNumber={numberToEdit === null}
              agents={agentsWithRoutingNumbers}
              admins={agentsWithRoutingNumbers.filter(
                (person) => person.role === "Administrator" || person.role === "Account Owner",
              )}
              accountOwnerId={accountOwnerId}
              sources={sources}
              onClose={closeModal}
              trackingNumber={numberToEdit || {}}
              onNumbersChanged={refreshPage}
            />
          )}
        </Section>
      </div>

      <Modal
        show={showChangePlanBar}
        onHide={() => setShowChangePlanBar(false)}
        dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
        contentClassName="tw-max-w-[900px] tw-max-h-[90vh] tw-w-full tw-flex tw-flex-col tw-gap-[32px]"
      >
        <Modal.Header title="Change Your Phone System Plan" closeButton />
        <Modal.Body className="tw-flex tw-flex-col tw-gap-[24px]">
          <Step1
            products={products}
            currentProductId={accountProduct?.product.details.productId}
            onSelectPlan={setNewProduct}
          />
        </Modal.Body>
      </Modal>

      <PhoneSystemChangeModal
        trackingNumbers={trackingNumbers}
        accountProduct={accountProduct}
        newProduct={newProduct}
        onClose={() => setNewProduct(null)}
        onChangePlan={handleChangeSubscription}
        open={Boolean(newProduct)}
      />
    </>
  );
};

TrackingNumbers.propTypes = {
  accountOwnerId: PropTypes.number.isRequired,
  agentsWithRoutingNumbers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  loadingSettings: PropTypes.bool.isRequired,
  sources: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  trackingNumbers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

const PageSizes = [10, 25, 50].map((i) => ({ label: i, value: i }));

const PageBar = ({ totalCount, currentOffset, pageSize, onChangePage, onChangePageSize }) => {
  const totalPages = Math.ceil(totalCount / pageSize);
  const currentPage = currentOffset ? currentOffset / pageSize + 1 : 1;
  const nextPage = currentPage === totalPages ? currentPage : currentPage + 1;
  const previousPage = currentPage === 1 ? currentPage : currentPage - 1;

  const onGoToPage = (pageNumber) => {
    const newOffset = (pageNumber - 1) * pageSize;
    onChangePage(newOffset);
  };

  const changePageSize = ({ value }) => {
    onChangePageSize(value);
  };

  return (
    <div className="tw-flex tw-flex-row tw-justify-between">
      <div className="tw-flex tw-flex-row tw-gap-8px tw-items-center">
        <span>Rows per page:</span>
        <Dropdown
          onChange={changePageSize}
          value={PageSizes.find((p) => p.value === pageSize)}
          options={PageSizes}
        />
      </div>
      <Pagination
        currentPage={currentPage}
        nextPage={nextPage}
        prevPage={previousPage}
        totalPages={totalPages}
        onGoToPage={onGoToPage}
      />
    </div>
  );
};

PageBar.propTypes = {
  totalCount: PropTypes.number.isRequired,
  currentOffset: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onChangePage: PropTypes.func.isRequired,
  onChangePageSize: PropTypes.func.isRequired,
};

const TableHeader = ({ children }) => <th className="tw-py-8px">{children}</th>;

const TableCell = ({ children }) => (
  <td className="tw-py-8px tw-pr-12px tw-max-w-[180px] tw-overflow-ellipsis tw-whitespace-nowrap tw-overflow-hidden">
    {children}
  </td>
);

const TrackingNumbersTable = ({ trackingNumbers, onClickEdit, sources }) => {
  const currentUser = useCurrentUser();
  const sourcesHash = useMemo(
    () =>
      sources?.reduce((acc, { value, label }) => {
        acc[value] = label;

        return acc;
      }, {}),
    [sources],
  );

  return (
    <div className="tw-width-auto tw-relative tw-flex tw-flex-col tw-flex-nowrap tw-overflow-auto tw-grow tw-shrink-0">
      <table className="tw-border-collapse tw-overflow-scroll tw-table-auto tw-flex-nowrap">
        <thead>
          <tr className="tw-text-12px tw-font-semibold tw-text-gray-75 tw-border-1px tw-border-solid tw-border-b-0 tw-border-x-0 tw-border-gray-15">
            <TableHeader>Tracking Number Name</TableHeader>
            <TableHeader>Number</TableHeader>
            <TableHeader>Members</TableHeader>
            <TableHeader>Source</TableHeader>
            <TableHeader>Routing Style</TableHeader>
            <TableHeader>
              Call Recording
              <Tooltip
                trigger={<CircleInfoSolidV6 size="m" className="tw-text-gray-75 tw-ml-[4px]" />}
                innerClassName="!tw-text-left tw-max-w-[213px]"
                content="Admins may update call recording settings by editing the tracking number."
                placement="top"
              />
            </TableHeader>
            {/* <th className="tw-text-12px tw-font-semibold tw-text-gray-75 tw-py-8px">Voicemmail</TableHeader> */}
            {currentUser.account.isAdmin ? <TableHeader>Edit</TableHeader> : ""}
          </tr>
        </thead>
        <tbody>
          {trackingNumbers.map((tn) => (
            <tr
              key={`tn-${tn.id}`}
              className="tw-h-36px tw-border-1px tw-border-solid tw-border-x-0 tw-border-gray-15"
            >
              <TableCell>{tn.name}</TableCell>
              <TableCell>{tn.number}</TableCell>
              <TableCell>
                <AvatarGroup size="medium">
                  {tn.users?.map((user) => (
                    <Tooltip
                      trigger={
                        <Avatar
                          key={`${tn.number}-${user.id}`}
                          size="medium"
                          src={user.avatarUrl}
                          alt={user.name}
                        >
                          {user.name}
                        </Avatar>
                      }
                      content={user.name}
                      placement="top"
                    />
                  ))}
                </AvatarGroup>
              </TableCell>
              <TableCell>{tn.source && sourcesHash[tn.source] ? sourcesHash[tn.source] : "--"}</TableCell>
              <TableCell>{LeadAssignInfo[tn.routingMethod].pretty}</TableCell>
              <TableCell>
                {tn.inboundRecording === "disabled" ? (
                  <span className="tw-text-14d tw-text-gray-50">
                    {CallRecordingInfo["inboundRecording"][tn.inboundRecording].pretty}
                  </span>
                ) : (
                  <div>
                    <div className="tw-text-14d tw-text-gray-50">
                      {CallRecordingInfo["inboundRecording"][tn.inboundRecording].pretty}
                    </div>
                    <div className="tw-text-12d tw-text-gray-50">
                      Notification Message:{" "}
                      {
                        CallRecordingInfo["inboundNotificationMessageCondition"][
                          tn.inboundNotificationMessageCondition
                        ].pretty
                      }
                    </div>
                  </div>
                )}
              </TableCell>
              {currentUser.account.isAdmin ? (
                <TableCell>
                  <IconButton schema="tertiary" onClick={() => onClickEdit(tn)}>
                    <PencilSolidV6 />
                  </IconButton>
                </TableCell>
              ) : (
                ""
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

TrackingNumbersTable.propTypes = {
  trackingNumbers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onClickEdit: PropTypes.func.isRequired,
  sources: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default TrackingNumbers;
